import { useCallback } from 'react';
import { log } from 'utils/log';
import { useUpdateItemFavoriteStatus } from 'hooks/useUpdateItemFavoriteStatus';
import { useSessionStorageContext } from 'contexts/SessionStorageContext/SessionStorageContext';
import { useSelectedSamplesContext } from 'contexts/SelectedSamplesContext/SelectedSamplesContext';
import { usePurchaseControllerContext } from 'contexts/PurchaseControllerContext/PurchaseControllerContext';
import {
    AuthenticatedActions,
    AuthenticatedUserActionArgs,
    AnyAuthenticatedUserAction,
} from 'types';
import { EventIds } from '../../../constants';

type HandleAttemptedActionType = (
    attemptedAction: AnyAuthenticatedUserAction,
) => void;

export const useHandleAttemptedAction = (): HandleAttemptedActionType => {
    const { updateFavorite } = useUpdateItemFavoriteStatus();
    const { getSamplesToAddToCollection } = useSessionStorageContext();
    const { toggleAllSamplesSelection } = useSelectedSamplesContext();
    const { startPurchase } = usePurchaseControllerContext();

    return useCallback(
        (attemptedAction) => {
            const { args } = attemptedAction;
            let item, samplesToAddToCollection;

            if (!Array.isArray(args)) {
                log.warn(
                    `An action, ${attemptedAction.type}, was passed to the post-login action handler but no arguments were provided`,
                    EventIds.NoArgumentsProvidedToPostLoginAction,
                );
                return;
            }

            switch (attemptedAction.type) {
                case AuthenticatedActions.downloadItem:
                    [item] =
                        args as AuthenticatedUserActionArgs[AuthenticatedActions.downloadItem];

                    startPurchase({ item });
                    break;
                case AuthenticatedActions.updateItemFavoriteState:
                    [item] =
                        args as AuthenticatedUserActionArgs[AuthenticatedActions.updateItemFavoriteState];

                    updateFavorite(item);
                    break;
                case AuthenticatedActions.addSamplesToCollection:
                    samplesToAddToCollection = getSamplesToAddToCollection();

                    if (samplesToAddToCollection.length) {
                        toggleAllSamplesSelection(samplesToAddToCollection);
                    }
                    break;
                default:
                    log.warn(
                        `An unsupported action, ${attemptedAction.type}, was passed to the post-login action handler.`,
                        EventIds.UnsupportedPostLoginAction,
                    );
                    break;
            }
        },
        [
            startPurchase,
            updateFavorite,
            getSamplesToAddToCollection,
            toggleAllSamplesSelection,
        ],
    );
};

export default useHandleAttemptedAction;
